import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { toDisplayString } from '../../utils/base';

const ToggleContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'size' && prop !== 'disabled',
})(({ theme, size, disabled }) => ({
    backgroundColor: theme.palette.figma.white[10],
    borderRadius: size === 'small' ? '12px' : '15px',
    padding: '2px',
    display: 'flex',
    height: size === 'small' ? '32px' : '40px',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease-in-out',
}));

const ToggleButton = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'active' && prop !== 'size' && prop !== 'disabled' && prop !== 'variableWidth',
})(({ theme, active, size, disabled, variableWidth }) => ({
    ...(variableWidth ? { flex: '1 1 auto', minWidth: 0, padding: '0 6px' } : { flex: 1, minWidth: '1px' }),
    height: '100%',
    minHeight: '1px',
    position: 'relative',
    borderRadius: size === 'small' ? '10px' : '13px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    backgroundColor: active ? theme.palette.figma.block : 'transparent',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: disabled
            ? (active ? theme.palette.figma.block : 'transparent')
            : (active ? theme.palette.figma.block : theme.palette.figma.white[5]),
    },
}));

const ToggleText = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'active' && prop !== 'size' && prop !== 'variableWidth',
})(({ theme, active, size, variableWidth }) => ({
    ...(variableWidth
        ? { position: 'relative' }
        : { position: 'absolute', left: 0, right: '0.33px', top: '50%', transform: 'translateY(-50%)' }
    ),
    fontSize: size === 'small' ? '12px' : '14px',
    fontWeight: active ? 600 : 400,
    color: active ? theme.palette.figma.white[100] : theme.palette.figma.white[80],
    letterSpacing: size === 'small' ? '-0.36px' : '-0.42px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease-in-out',
}));

const Toggle = ({
    options = ['체력', '기타', 'Text03'],
    value = 0, // Changed from activeIndex to value for consistency
    onChange = () => { },
    size = 'medium',
    disabled = false,
    variableWidth = false, // true면 글자 길이에 따라 탭 너비가 달라짐
    sx = {},
    ...props
}) => {
    const handleOptionClick = index => {
        if (!disabled && onChange && index !== value) {
            onChange(index);
        }
    };

    return (
        <ToggleContainer
            size={size}
            disabled={disabled}
            sx={sx}
            {...props}
        >
            {options.map((option, index) => (
                <ToggleButton
                    key={index}
                    active={index === value}
                    size={size}
                    disabled={disabled}
                    variableWidth={variableWidth}
                    onClick={() => handleOptionClick(index)}
                >
                    <ToggleText
                        active={index === value}
                        size={size}
                        variableWidth={variableWidth}
                    >
                        {toDisplayString(option, '')}
                    </ToggleText>
                </ToggleButton>
            ))}
        </ToggleContainer>
    );
};

export default Toggle;